import * as React from 'react'

import { computed, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { Layer, Source } from 'react-map-gl/mapbox'

import { SitemapItemShapeType, SitemapLineArrowPosition } from '~/client/graph'
import {
  ITEM_EDITOR_ARROW_LINE_LAYER,
  ITEM_EDITOR_SHAPE_FILL_LAYER,
  ITEM_EDITOR_SHAPE_LINE_LAYER,
  POLYLINE_ADDITIONAL_EDITABLE_POINTS_LAYER,
  SHAPE_EDITABLE_POINTS_LAYER,
  SHAPE_SELECTED_POINTS_LAYER,
} from '~/client/src/shared/components/MapBoxViewer/mapboxConstants'
import MapBoxViewerStore, {
  MAX_ITEM_ZOOM_LEVEL,
  PROJECT_MARKER_ZOOM_LEVEL,
} from '~/client/src/shared/components/MapBoxViewer/MapBoxViewer.store'
import MapViewItemBase from '~/client/src/shared/components/SitemapHelpers/models/MapViewItemBase'
import PolyLineShapeCoordinates from '~/client/src/shared/components/SitemapHelpers/models/PolyLineShapeCoordinates'
import MapViewItemDrawnPart from '~/client/src/shared/components/SitemapHelpers/models/SitemapItemDrawnPart'
import SitemapPolyLineProperties from '~/client/src/shared/components/SitemapHelpers/models/SitemapPolyLineProperties'
import ThemeMode from '~/client/src/shared/utils/ThemeModeManager'

import MapBoxEditorStore from '../../stores/MapBoxEditor.store'
import MapViewItemsSetupStore from '../../stores/MapViewItemsSetup.store'

import Colors from '~/client/src/shared/theme.module.scss'

interface IProps {
  store: MapViewItemsSetupStore
  mapBoxViewerStore: MapBoxViewerStore
  mapBoxEditorStore: MapBoxEditorStore
}

type ShapePointGeoJson =
  | GeoJSON.Feature<GeoJSON.Geometry>
  | GeoJSON.FeatureCollection<GeoJSON.Geometry>
  | string

const BLUE_CIRCLE_COLOR = 'blue'
const WHITE_CIRCLE_COLOR = 'white'
const SHAPE_EDITABLE_POINTS_ID = 'shape_editable_points_data'
const SHAPE_SELECTED_POINTS_ID = 'shape_selected_points_data'

@observer
export default class GlobeViewEditableItem extends React.Component<IProps> {
  @observable private store: MapViewItemsSetupStore = null
  @observable private mapBoxEditorStore: MapBoxEditorStore = null
  @observable private mapBoxViewerStore: MapBoxViewerStore = null
  public constructor(props: IProps) {
    super(props)

    this.store = props.store
    this.mapBoxEditorStore = props.mapBoxEditorStore
    this.mapBoxViewerStore = props.mapBoxViewerStore
    makeObservable(this)
  }

  // One tree for every part: a remounted <Source> drops its layers, and react-map-gl re-adds
  // them on top of the style, over the item's own marker.
  public render(): JSX.Element {
    if (!this.mapViewItem || !this.mapViewItem.isDisplayed) {
      return null
    }
    return this.renderShape()
  }

  private get mapViewItem(): MapViewItemBase {
    return this.store.selectedMapViewItem
  }

  @computed
  private get fillShapeGeoJson(): ShapePointGeoJson {
    const {
      sitemapItem: { shapeCoordinates },
      globeViewItemProperties: { shapeProperties },
      id: itemId,
    } = this.mapViewItem

    return shapeCoordinates.getShapePolygonFeature({
      fill: ThemeMode.getHEXColor(shapeProperties?.fillColor),
      'fill-opacity': shapeProperties?.fillOpacity,
      itemId,
    })
  }

  @computed
  private get lineShapeGeoJson(): ShapePointGeoJson {
    const {
      sitemapItem: { shapeCoordinates },
      globeViewItemProperties: { shapeProperties },
      id: itemId,
    } = this.mapViewItem

    return shapeCoordinates.getShapeLineStringFeature({
      stroke: ThemeMode.getHEXColor(shapeProperties?.lineColor),
      'stroke-width': shapeProperties?.lineWidth,
      'text-width': 18 + shapeProperties?.lineWidth * 5,
      itemId,
    })
  }

  @computed
  private get arrowsShapeGeoJson(): ShapePointGeoJson {
    const {
      sitemapItem: { shapeCoordinates },
      globeViewItemProperties: { shapeProperties },
      id: itemId,
    } = this.mapViewItem

    if (shapeCoordinates?.coordinates?.length > 1) {
      const arrowPosition = (shapeProperties as SitemapPolyLineProperties)
        ?.arrowPosition
      if (
        shapeProperties?.type !== SitemapItemShapeType.Polyline ||
        arrowPosition === SitemapLineArrowPosition.None
      ) {
        return null
      }
      return {
        type: 'Feature',
        properties: {
          stroke: ThemeMode.getHEXColor(shapeProperties?.lineColor),
          'text-width': 25 + shapeProperties?.lineWidth * 5,
          'text-field': [
            SitemapLineArrowPosition.StartEnd,
            SitemapLineArrowPosition.StartMiddleEnd,
          ].includes(arrowPosition)
            ? '►'
            : '◄',
          'text-color': ThemeMode.getHEXColor(shapeProperties?.lineColor),
          itemId,
        },
        geometry: shapeCoordinates.getShapeLineStringFeature({}).geometry,
      }
    }
  }

  @computed
  private get boundingBoxGeoJson(): ShapePointGeoJson {
    if (this.mapViewItem.sitemapItem.shapeCoordinates?.coordinates?.length) {
      const bearing = this.mapBoxViewerStore.viewport?.bearing || 0
      return this.mapViewItem.sitemapItem.shapeCoordinates.getBoundingBoxFeature(
        {},
        bearing,
      )
    }
  }

  private renderShape = (): JSX.Element => {
    const { selectedShapePointIdx } = this.mapBoxEditorStore
    const { selectedMapViewItemDrawnPart } = this.store
    if (!this.mapViewItem.sitemapItem?.shapeCoordinates?.coordinates?.length) {
      return null
    }
    return (
      <>
        {this.renderFillLayer()}
        {this.renderLineLayer()}
        {this.renderArrowsLayer()}
        {selectedMapViewItemDrawnPart === MapViewItemDrawnPart.Shape &&
          this.renderShapePoint(
            this.shapeEditablePointsGeoJson,
            SHAPE_EDITABLE_POINTS_ID,
            SHAPE_EDITABLE_POINTS_LAYER,
            WHITE_CIRCLE_COLOR,
          )}
        {selectedShapePointIdx !== null &&
          this.renderShapePoint(
            this.shapeSelectedPointGeoJson,
            SHAPE_SELECTED_POINTS_ID,
            SHAPE_SELECTED_POINTS_LAYER,
            BLUE_CIRCLE_COLOR,
          )}
        {selectedMapViewItemDrawnPart !== MapViewItemDrawnPart.Label &&
          selectedMapViewItemDrawnPart !== MapViewItemDrawnPart.Icon &&
          this.renderBoundingBox()}
      </>
    )
  }

  private renderFillLayer = (): JSX.Element => {
    if (
      (this.mapViewItem.sitemapItem.shapeCoordinates?.coordinates?.length < 3 &&
        this.mapViewItem.sitemapItem.shapeCoordinates?.type !==
          SitemapItemShapeType.Circle) ||
      (this.mapViewItem.sitemapItem.shapeCoordinates.type ===
        SitemapItemShapeType.Polyline &&
        !this.mapViewItem.sitemapItem.shapeCoordinates.isClosed) ||
      !this.mapViewItem.globeViewItemProperties.shapeProperties?.isDisplayed
    ) {
      return null
    }
    // One id in every part: react-map-gl fixes a layer's id at mount.
    return (
      <Source
        id="item_editor_fill_shape_data"
        type="geojson"
        data={this.fillShapeGeoJson}
      >
        <Layer
          id={ITEM_EDITOR_SHAPE_FILL_LAYER}
          type="fill"
          paint={{
            'fill-color': ['get', 'fill'],
            'fill-opacity': ['get', 'fill-opacity'],
          }}
          maxzoom={MAX_ITEM_ZOOM_LEVEL}
          minzoom={PROJECT_MARKER_ZOOM_LEVEL}
        />
      </Source>
    )
  }

  private renderLineLayer = (): JSX.Element => {
    if (
      (this.mapViewItem.sitemapItem.shapeCoordinates?.coordinates?.length < 2 &&
        this.mapViewItem.sitemapItem.shapeCoordinates?.type !==
          SitemapItemShapeType.Circle) ||
      !this.mapViewItem.globeViewItemProperties.shapeProperties?.isDisplayed
    ) {
      return null
    }

    return (
      <Source
        id="item_editor_shape_line_data"
        type="geojson"
        data={this.lineShapeGeoJson}
      >
        <Layer
          id={ITEM_EDITOR_SHAPE_LINE_LAYER}
          type="line"
          layout={{
            'line-join': 'none',
            'line-cap': 'round',
          }}
          paint={{
            'line-color': ['get', 'stroke'],
            'line-width': ['get', 'stroke-width'],
          }}
          maxzoom={MAX_ITEM_ZOOM_LEVEL}
          minzoom={PROJECT_MARKER_ZOOM_LEVEL}
        />
      </Source>
    )
  }

  private renderArrowsLayer = (): JSX.Element => {
    if (
      this.mapViewItem.sitemapItem.shapeCoordinates.isClosed ||
      !this.mapViewItem.globeViewItemProperties.shapeProperties?.isDisplayed
    ) {
      return null
    }

    return (
      <Source
        id="item_editor_arrows_shape_data"
        type="geojson"
        data={this.arrowsShapeGeoJson}
      >
        <Layer
          id={ITEM_EDITOR_ARROW_LINE_LAYER}
          type="symbol"
          layout={{
            'text-field': ['get', 'text-field'],
            'text-size': ['get', 'text-width'],
            'symbol-placement': 'line',
            'symbol-avoid-edges': true,
            'symbol-spacing': [
              'interpolate',
              // Set the exponential rate of change to 0.5
              ['linear'],
              ['zoom'],
              // When zoom is 10, spacing will be 1px.
              10,
              1,
              // When zoom is 20 or higher, spacing will be 60px.
              20,
              60,
            ],
            'text-rotation-alignment': 'map',
            'text-keep-upright': false,
          }}
          paint={{
            'text-color': ['get', 'text-color'],
          }}
          maxzoom={MAX_ITEM_ZOOM_LEVEL}
          minzoom={PROJECT_MARKER_ZOOM_LEVEL}
        />
      </Source>
    )
  }

  private renderBoundingBox = (): JSX.Element => {
    if (
      !this.mapViewItem.sitemapItem?.shapeCoordinates?.coordinates?.length ||
      !this.mapViewItem.globeViewItemProperties.shapeProperties?.isDisplayed
    ) {
      return null
    }
    return (
      <Source
        id="bounding_box_editor_data"
        type="geojson"
        data={this.boundingBoxGeoJson}
      >
        <Layer
          id="bounding_box_editor_line"
          type="line"
          paint={{
            'line-color': ThemeMode.getHEXColor(Colors.primary60),
            'line-width': 2,
          }}
          maxzoom={MAX_ITEM_ZOOM_LEVEL}
          minzoom={PROJECT_MARKER_ZOOM_LEVEL}
        />
      </Source>
    )
  }

  private renderShapePoint(
    geoJson: ShapePointGeoJson,
    id: string,
    layerId: string,
    circleColor: string,
  ): JSX.Element {
    return (
      <>
        <Source id={id} type="geojson" data={geoJson}>
          <Layer
            id={layerId}
            type="circle"
            paint={{
              'circle-radius': ['get', 'circle-radius'],
              'circle-color': circleColor,
              'circle-stroke-width': 1,
              'circle-stroke-color': 'blue',
            }}
            maxzoom={MAX_ITEM_ZOOM_LEVEL}
            minzoom={PROJECT_MARKER_ZOOM_LEVEL}
          />
        </Source>
        {this.mapViewItem.sitemapItem.shapeCoordinates.type ===
          SitemapItemShapeType.Polyline &&
          this.renderPolylineAdditionalPoints()}
      </>
    )
  }

  private renderPolylineAdditionalPoints(): JSX.Element {
    return (
      <Source
        id="polyline_additional_editable_points_data"
        type="geojson"
        data={this.polylineAdditionalEditablePointsGeoJson}
      >
        <Layer
          id={POLYLINE_ADDITIONAL_EDITABLE_POINTS_LAYER}
          type="circle"
          paint={{
            'circle-radius': ['get', 'circle-radius'],
            'circle-color': ['get', 'circle-color'],
          }}
          maxzoom={MAX_ITEM_ZOOM_LEVEL}
          minzoom={PROJECT_MARKER_ZOOM_LEVEL}
        />
      </Source>
    )
  }

  @computed
  private get shapeSelectedPointGeoJson():
    | GeoJSON.Feature<GeoJSON.Geometry>
    | GeoJSON.FeatureCollection<GeoJSON.Geometry>
    | string {
    const { selectedShapePointIdx } = this.mapBoxEditorStore
    const coords =
      this.mapViewItem.sitemapItem.shapeCoordinates.shapeEditorPointCoordinates.filter(
        (p, i) => i === selectedShapePointIdx,
      )
    return {
      type: 'FeatureCollection',
      features: coords?.map((c, idx) => ({
        type: 'Feature',
        properties: {
          idx,
          'circle-radius':
            this.mapViewItem.globeViewItemProperties.shapeProperties
              ?.lineWidth + 5 || 8,
          shapeType: this.mapViewItem.sitemapItem.shapeCoordinates.type,
        },
        geometry: {
          type: 'Point',
          coordinates: c,
        },
      })),
    }
  }

  @computed
  private get shapeEditablePointsGeoJson(): ShapePointGeoJson {
    if (
      !this.mapViewItem.globeViewItemProperties.shapeProperties?.isDisplayed
    ) {
      return null
    }
    const coords =
      this.mapViewItem.sitemapItem.shapeCoordinates.shapeEditorPointCoordinates

    return {
      type: 'FeatureCollection',
      features: coords?.map((c, idx) => ({
        type: 'Feature',
        properties: {
          idx,
          'circle-radius':
            this.mapViewItem.globeViewItemProperties.shapeProperties
              ?.lineWidth + 5 || 8,
          shapeType: this.mapViewItem.sitemapItem.shapeCoordinates.type,
        },
        geometry: {
          type: 'Point',
          coordinates: c,
        },
      })),
    }
  }

  @computed
  private get polylineAdditionalEditablePointsGeoJson(): ShapePointGeoJson {
    const {
      sitemapItem: { shapeCoordinates },
      globeViewItemProperties: { shapeProperties },
    } = this.mapViewItem
    const coords = (shapeCoordinates as PolyLineShapeCoordinates)
      .shapeAdditionalEditorPointCoordinates

    return {
      type: 'FeatureCollection',
      features: coords?.map((c, idx) => ({
        type: 'Feature',
        properties: {
          idx,
          shapeType: shapeCoordinates.type,
          'circle-radius': shapeProperties?.lineWidth + 3 || 5,
          'circle-color': ThemeMode.getHEXColor(shapeProperties?.lineColor),
        },
        geometry: {
          type: 'Point',
          coordinates: c,
        },
      })),
    }
  }
}
